import { useCallback, useState } from 'react';
import { Button } from '@/components/common/Button';
import type { HistoryFilters } from './types';

interface HistoryFiltersPanelProps {
  filters: HistoryFilters;
  onFilterChange: (filters: Partial<HistoryFilters>) => void;
  onClearFilters: () => void;
  onSearch: () => void;
  dateError: string | null;
}

export function HistoryFiltersPanel({
  filters,
  onFilterChange,
  onClearFilters,
  onSearch,
  dateError,
}: HistoryFiltersPanelProps) {
  const [localSearch, setLocalSearch] = useState(filters.searchQuery);

  const handleSearchKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        onFilterChange({ searchQuery: localSearch });
        onSearch();
      }
    },
    [localSearch, onFilterChange, onSearch],
  );

  const handleSearchBlur = useCallback(() => {
    if (localSearch !== filters.searchQuery) {
      onFilterChange({ searchQuery: localSearch });
      onSearch();
    }
  }, [localSearch, filters.searchQuery, onFilterChange, onSearch]);

  const hasActiveFilters =
    filters.type !== 'all' ||
    filters.status !== 'all' ||
    filters.dateFrom !== '' ||
    filters.dateTo !== '' ||
    filters.searchQuery !== '';

  return (
    <div className="mb-4 flex flex-wrap items-end gap-3" data-testid="history-filters">
      {/* Type filter */}
      <div>
        <label
          htmlFor="history-type-filter"
          className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.5px] text-[#999]"
        >
          Type
        </label>
        <select
          id="history-type-filter"
          value={filters.type}
          onChange={(e) => {
            onFilterChange({ type: e.target.value });
            onSearch();
          }}
          className="rounded-lg border border-[#e0e0e0] px-3 py-2 text-sm outline-none focus:border-[#6B0F1A]"
          data-testid="history-type-filter"
        >
          <option value="all">All Types</option>
          <option value="payment">Payment</option>
          <option value="fx_trade">FX Trade</option>
          <option value="incoming">Incoming</option>
        </select>
      </div>

      {/* Status filter */}
      <div>
        <label
          htmlFor="history-status-filter"
          className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.5px] text-[#999]"
        >
          Status
        </label>
        <select
          id="history-status-filter"
          value={filters.status}
          onChange={(e) => {
            onFilterChange({ status: e.target.value });
            onSearch();
          }}
          className="rounded-lg border border-[#e0e0e0] px-3 py-2 text-sm outline-none focus:border-[#6B0F1A]"
          data-testid="history-status-filter"
        >
          <option value="all">All Statuses</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="processing">Processing</option>
          <option value="completed">Completed</option>
          <option value="executed">Executed</option>
          <option value="rejected">Rejected</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      {/* Date From */}
      <div>
        <label
          htmlFor="history-date-from"
          className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.5px] text-[#999]"
        >
          From
        </label>
        <input
          id="history-date-from"
          type="date"
          value={filters.dateFrom}
          onChange={(e) => {
            onFilterChange({ dateFrom: e.target.value });
            onSearch();
          }}
          className="rounded-lg border border-[#e0e0e0] px-3 py-2 text-sm outline-none focus:border-[#6B0F1A]"
          data-testid="history-date-from"
        />
      </div>

      {/* Date To */}
      <div>
        <label
          htmlFor="history-date-to"
          className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.5px] text-[#999]"
        >
          To
        </label>
        <input
          id="history-date-to"
          type="date"
          value={filters.dateTo}
          onChange={(e) => {
            onFilterChange({ dateTo: e.target.value });
            onSearch();
          }}
          className="rounded-lg border border-[#e0e0e0] px-3 py-2 text-sm outline-none focus:border-[#6B0F1A]"
          data-testid="history-date-to"
        />
        {dateError && (
          <p className="mt-1 text-xs text-[#c62828]" role="alert" data-testid="history-date-error">
            {dateError}
          </p>
        )}
      </div>

      {/* Search */}
      <div className="min-w-[200px]">
        <label
          htmlFor="history-search"
          className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.5px] text-[#999]"
        >
          Search
        </label>
        <input
          id="history-search"
          type="text"
          placeholder="Search sender, beneficiary, reference..."
          value={localSearch}
          onChange={(e) => setLocalSearch(e.target.value)}
          onKeyDown={handleSearchKeyDown}
          onBlur={handleSearchBlur}
          className="w-full rounded-lg border border-[#e0e0e0] px-3 py-2 text-sm outline-none focus:border-[#6B0F1A]"
          data-testid="history-search"
        />
      </div>

      {/* Clear Filters */}
      {hasActiveFilters && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setLocalSearch('');
            onClearFilters();
          }}
          data-testid="history-clear-filters"
        >
          Clear Filters
        </Button>
      )}
    </div>
  );
}
